import React, { useState, useEffect, useRef } from 'react';

const QUESTIONS = [
  { id: 'description', q: "Tell me about your business — what do you do and who do you serve?" },
  { id: 'services', q: "What are your main services or products?" },
  { id: 'team', q: "How is your team structured? (e.g. managers, field staff, office staff)" },
  { id: 'challenges', q: "What are your biggest operational challenges right now?" },
  { id: 'goals', q: "What would you most like Aetheris to help you with?" },
];

const InterviewWizard = ({ company, token, onComplete }) => {
  const [messages, setMessages] = useState([
    { role: 'ai', text: `Hi! I'm Aetheris. I'll be setting up your personalized AI assistant for ${company.companyName}. Let's start — ${QUESTIONS[0].q}` }
  ]);
  const [input, setInput] = useState('');
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const bottomRef = useRef();

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput('');
    setMessages(m => [...m, { role: 'user', text: userMsg }]);
    setLoading(true);

    const newAnswers = { ...answers, [QUESTIONS[step].id]: userMsg };
    setAnswers(newAnswers);

    const nextStep = step + 1;
    if (nextStep < QUESTIONS.length) {
      setStep(nextStep);
      setTimeout(() => {
        setMessages(m => [...m, { role: 'ai', text: QUESTIONS[nextStep].q }]);
        setLoading(false);
      }, 800);
    } else {
      // All questions answered — complete onboarding
      try {
        const r = await fetch('/api/onboarding/complete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token, answers: newAnswers })
        });
        const data = await r.json();
        if (data.success) {
          setMessages(m => [...m, { role: 'ai', text: `Perfect! I've set up your Aetheris instance for ${company.companyName}. Your account is ready — check your email at ${company.adminEmail} for your login credentials.` }]);
          setDone(true);
        } else {
          setMessages(m => [...m, { role: 'ai', text: 'There was an issue completing setup. Please contact your administrator.' }]);
        }
      } catch {
        setMessages(m => [...m, { role: 'ai', text: 'Connection error. Please try again.' }]);
      }
      setLoading(false);
    }
  };

  return (
    <div style={{minHeight:'100vh',background:'#0f172a',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{maxWidth:'640px',width:'100%'}}>
        <div style={{textAlign:'center',marginBottom:'24px'}}>
          <div style={{fontSize:'32px',marginBottom:'8px'}}>🧠</div>
          <h2 style={{color:'#e2e8f0',margin:0}}>Setting up {company.companyName}</h2>
          <p style={{color:'#64748b',fontSize:'13px',marginTop:'4px'}}>Question {Math.min(step+1, QUESTIONS.length)} of {QUESTIONS.length}</p>
          <div style={{background:'#1e293b',borderRadius:'8px',height:'6px',marginTop:'12px'}}>
            <div style={{background:'#7c3aed',height:'6px',borderRadius:'8px',width:`${(Math.min(step+1,QUESTIONS.length)/QUESTIONS.length)*100}%`,transition:'width 0.3s'}}></div>
          </div>
        </div>

        <div style={{background:'#1e293b',borderRadius:'16px',border:'1px solid #334155',overflow:'hidden'}}>
          <div style={{padding:'20px',maxHeight:'400px',overflowY:'auto'}}>
            {messages.map((m, i) => (
              <div key={i} style={{marginBottom:'16px',display:'flex',justifyContent:m.role==='user'?'flex-end':'flex-start'}}>
                <div style={{
                  maxWidth:'80%',padding:'12px 16px',borderRadius:'12px',fontSize:'14px',
                  background: m.role==='user' ? '#7c3aed' : '#0f172a',
                  color: '#e2e8f0'
                }}>{m.text}</div>
              </div>
            ))}
            {loading && <div style={{color:'#64748b',fontSize:'13px',padding:'8px'}}>Aetheris is thinking...</div>}
            <div ref={bottomRef}/>
          </div>

          {!done && (
            <div style={{padding:'16px',borderTop:'1px solid #334155',display:'flex',gap:'8px'}}>
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && send()}
                placeholder="Type your answer..."
                style={{
                  flex:1,padding:'10px 16px',borderRadius:'8px',background:'#0f172a',
                  border:'1px solid #334155',color:'#e2e8f0',fontSize:'14px'
                }}
              />
              <button onClick={send} disabled={loading} style={{
                background:'#7c3aed',color:'white',padding:'10px 20px',
                borderRadius:'8px',border:'none',cursor:'pointer',fontSize:'14px'
              }}>Send</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const OnboardingPage = () => {
  const [status, setStatus] = useState('loading');
  const [company, setCompany] = useState(null);
  const [token, setToken] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token');
    if (!t) { setStatus('invalid'); return; }
    setToken(t);

    fetch(`/api/onboarding/verify?token=${t}`)
      .then(r => r.json())
      .then(data => {
        if (data.valid) { setCompany(data); setStatus('welcome'); }
        else { setError(data.error); setStatus('invalid'); }
      })
      .catch(() => setStatus('invalid'));
  }, []);

  if (status === 'loading') return (
    <div style={{minHeight:'100vh',background:'#0f172a',display:'flex',alignItems:'center',justifyContent:'center',color:'#94a3b8'}}>
      Verifying invite...
    </div>
  );

  if (status === 'invalid') return (
    <div style={{minHeight:'100vh',background:'#0f172a',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{textAlign:'center',color:'#f87171'}}>
        <div style={{fontSize:'48px',marginBottom:'16px'}}>❌</div>
        <h2 style={{color:'#f87171'}}>Invalid or expired invite</h2>
        <p style={{color:'#64748b'}}>{error || 'This onboarding link is no longer valid.'}</p>
      </div>
    </div>
  );

  if (status === 'interview') return <InterviewWizard company={company} token={token} onComplete={() => setStatus('done')} />;

  if (status === 'done') return (
    <div style={{minHeight:'100vh',background:'#0f172a',display:'flex',alignItems:'center',justifyContent:'center'}}>
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:'64px',marginBottom:'16px'}}>🎉</div>
        <h1 style={{color:'#34d399'}}>Setup Complete!</h1>
        <p style={{color:'#94a3b8'}}>Check your email for login credentials.</p>
      </div>
    </div>
  );

  return (
    <div style={{minHeight:'100vh',background:'#0f172a',display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}>
      <div style={{maxWidth:'600px',width:'100%'}}>
        <div style={{textAlign:'center',marginBottom:'40px'}}>
          <div style={{fontSize:'48px',marginBottom:'16px'}}>🧠</div>
          <h1 style={{color:'#e2e8f0',margin:0}}>Welcome to Sentari OS</h1>
          <p style={{color:'#64748b',marginTop:'8px'}}>You've been invited to set up Aetheris for <strong style={{color:'#7c3aed'}}>{company?.companyName}</strong></p>
        </div>
        <div style={{background:'#1e293b',borderRadius:'16px',padding:'32px',border:'1px solid #334155'}}>
          <h2 style={{color:'#e2e8f0',marginBottom:'8px'}}>Let's get started</h2>
          <p style={{color:'#94a3b8',marginBottom:'24px',fontSize:'14px'}}>
            We'll ask you a few questions about your business to set up your personalized AI assistant.
          </p>
          <div style={{background:'#0f172a',borderRadius:'12px',padding:'20px',marginBottom:'24px'}}>
            <div style={{color:'#64748b',fontSize:'12px',marginBottom:'12px'}}>YOUR INVITE DETAILS</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'12px'}}>
              <div>
                <div style={{color:'#64748b',fontSize:'11px'}}>COMPANY</div>
                <div style={{color:'#e2e8f0',fontSize:'14px',fontWeight:'bold'}}>{company?.companyName}</div>
              </div>
              <div>
                <div style={{color:'#64748b',fontSize:'11px'}}>INDUSTRY</div>
                <div style={{color:'#e2e8f0',fontSize:'14px',fontWeight:'bold'}}>{company?.industry || 'Not specified'}</div>
              </div>
              <div>
                <div style={{color:'#64748b',fontSize:'11px'}}>ADMIN EMAIL</div>
                <div style={{color:'#e2e8f0',fontSize:'14px'}}>{company?.adminEmail}</div>
              </div>
            </div>
          </div>
          <button style={{
            width:'100%',background:'#7c3aed',color:'white',padding:'14px',
            borderRadius:'10px',border:'none',cursor:'pointer',fontSize:'16px',fontWeight:'bold'
          }} onClick={() => setStatus('interview')}>
            🚀 Begin Setup Interview
          </button>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
